import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Gauge, UserList, PencilSimple, UsersThree, Tag, Ticket, Storefront, Lifebuoy, Crown, ShieldStar, ArrowLeft } from "@phosphor-icons/react";
import { Shell } from "@/components/Shell";
import { useApp } from "@/context/AppContext";

export const ADMIN_NAV = [
  { to: "/admin", label: "Übersicht", Icon: Gauge, end: true, id: "dashboard" },
  { to: "/admin/registrations", label: "Registrierungen", Icon: UserList, id: "registrations" },
  { to: "/admin/content", label: "Inhalte", Icon: PencilSimple, id: "content" },
  { to: "/admin/guests", label: "Gastzugänge", Icon: UsersThree, id: "guests" },
  { to: "/admin/buddies", label: "Buddy-Verwaltung", Icon: Tag, id: "buddies" },
  { to: "/admin/codes", label: "Aktivierungscodes", Icon: Ticket, id: "codes" },
  { to: "/admin/shop", label: "Shop-Verwaltung", Icon: Storefront, id: "shop" },
  { to: "/admin/tickets", label: "Support-Tickets", Icon: Lifebuoy, id: "tickets" },
  // Premium-Verwaltung ist vorbereitet, aber noch nicht aktiv. `hidden: false` setzen, um sie in der Navigation anzuzeigen.
  { to: "/admin/premium", label: "Premium-Verwaltung", Icon: Crown, id: "premium", hidden: true },
];

export default function AdminLayout() {
  const { user, authChecked } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (authChecked && (!user || !user.is_admin)) navigate("/dashboard");
  }, [authChecked, user, navigate]);

  const visible = ADMIN_NAV.filter((n) => !n.hidden);
  const current = ADMIN_NAV
    .filter((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))
    .sort((a, b) => b.to.length - a.to.length)[0] || ADMIN_NAV[0];
  const onDashboard = location.pathname === "/admin";

  const linkCls = (isActive) =>
    `flex items-center gap-2.5 px-3 py-2.5 rounded-2xl border-2 font-heading text-sm transition-all whitespace-nowrap ${
      isActive
        ? "text-white border-transparent shadow-[0_4px_16px_color-mix(in_srgb,var(--app-primary)_45%,transparent)]"
        : "border-[var(--app-border)] bg-[var(--app-surface)] text-[color:var(--app-strong)] hover:-translate-y-0.5"
    }`;

  return (
    <Shell wide back="/dashboard">
      <div className="lg:flex lg:gap-6" data-testid="admin-layout">
        {/* Sidebar — Desktop */}
        <aside className="hidden lg:block w-60 shrink-0">
          <div className="sb-card p-3 sticky top-2">
            <div className="flex items-center gap-2 px-2 py-2 mb-2">
              <ShieldStar size={24} weight="duotone" style={{ color: "var(--app-strong)" }} />
              <span className="font-heading font-semibold text-base">Admin-Bereich</span>
            </div>
            <nav className="flex flex-col gap-1.5" data-testid="admin-sidebar">
              {visible.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.id}`}
                  className={({ isActive }) => linkCls(isActive)}
                  style={({ isActive }) => (isActive ? { background: "var(--app-primary)" } : {})}>
                  <n.Icon size={20} weight="bold" className="shrink-0" />
                  <span className="truncate">{n.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        </aside>

        {/* Nav — Mobile (horizontal scroll) */}
        <div className="lg:hidden -mx-1 mb-4 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 px-1 w-max" data-testid="admin-nav-mobile">
            {visible.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-m-${n.id}`}
                className={({ isActive }) => linkCls(isActive)}
                style={({ isActive }) => (isActive ? { background: "var(--app-primary)" } : {})}>
                <n.Icon size={18} weight="bold" className="shrink-0" />
                <span>{n.label}</span>
              </NavLink>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 mb-5">
            {!onDashboard && (
              <button data-testid="admin-back-dashboard" onClick={() => navigate("/admin")} aria-label="Zurück zur Übersicht"
                className="w-10 h-10 rounded-2xl border-2 border-[var(--app-border)] bg-[var(--app-surface)] flex items-center justify-center active:scale-90 transition-transform shrink-0">
                <ArrowLeft size={20} weight="bold" style={{ color: "var(--app-strong)" }} />
              </button>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <current.Icon size={28} weight="duotone" style={{ color: "var(--app-strong)" }} className="shrink-0" />
              <h1 className="text-2xl font-heading font-semibold truncate" data-testid="admin-section-title">{current.label}</h1>
            </div>
          </div>
          <motion.div key={location.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <Outlet />
          </motion.div>
        </div>
      </div>
    </Shell>
  );
}
